"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Sparkles, Code2, Zap, Check } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const benefits = [
  "Multi-Agent AI System",
  "Deep Context Awareness",
  "Instant Deployments",
  "Full-Stack Support",
];

export function SolutionSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  const reducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);

  return (
    <section
      ref={ref}
      id="solution"
      className="relative min-h-screen w-full py-32 flex items-center justify-center overflow-hidden"
    >
      {/* Beautiful gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f0f] via-[#1a1a2e] to-[#16213e]" />

        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(124,58,237,0.4) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/3 w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(6,182,212,0.4) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
          animate={{
            x: [0, -40, 0],
            y: [0, 40, 0],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(236,72,153,0.3) 0%, transparent 70%)",
            filter: "blur(80px)",
          }}
          animate={{
            scale: [1, 1.3, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Particle field */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.3 + Math.random() * 0.4,
            }}
            animate={{
              y: [0, -50, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <motion.div
        className="relative z-10 container mx-auto px-6 text-center"
        style={reducedMotion ? {} : { scale, opacity }}
      >
        {/* Animated logo */}
        <motion.div
          className="mb-10"
          initial={{ scale: 0, rotate: -180 }}
          animate={isInView ? { scale: 1, rotate: 0 } : {}}
          transition={{
            duration: reducedMotion ? 0 : 1,
            type: "spring",
            stiffness: 100,
            damping: 15,
          }}
        >
          <div className="relative inline-flex">
            {/* Glow rings */}
            <motion.div
              className="absolute inset-0 rounded-3xl"
              style={{
                background: "linear-gradient(135deg, #7c3aed, #06b6d4)",
                filter: "blur(20px)",
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <div className="relative w-28 h-28 rounded-3xl bg-gradient-to-br from-violet-600 via-purple-600 to-cyan-500 p-[2px]">
              <div className="w-full h-full rounded-3xl bg-[#1a1a2e] flex items-center justify-center">
                <Code2 className="w-14 h-14 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section label */}
        <motion.div
          className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/30 rounded-full mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <Zap className="w-4 h-4 text-yellow-400" />
          <span className="text-sm font-semibold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
            INTRODUCING VIBE
          </span>
          <Sparkles className="w-4 h-4 text-cyan-400" />
        </motion.div>

        {/* Main headline */}
        <motion.h2
          className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <span className="block bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            VIBE CHANGES
          </span>
          <span className="block bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            EVERYTHING
          </span>
        </motion.h2>

        {/* Tagline */}
        <motion.p
          className="max-w-2xl mx-auto text-xl md:text-2xl text-white/60 font-light leading-relaxed mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          Your AI-powered development platform that understands context,
          writes code, and ships products{" "}
          <span className="text-white font-semibold">10x faster</span>
        </motion.p>

        {/* Benefits */}
        <motion.div
          className="flex flex-wrap justify-center gap-4 mb-12"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.9 + index * 0.1, duration: 0.5 }}
              whileHover={{
                scale: 1.05,
                backgroundColor: "rgba(124, 58, 237, 0.1)",
                borderColor: "rgba(124, 58, 237, 0.3)",
              }}
            >
              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span className="text-sm font-medium text-white/80">{benefit}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Glowing divider */}
        <motion.div
          className="w-32 h-1 mx-auto rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-cyan-500"
          initial={{ width: 0, opacity: 0 }}
          animate={isInView ? { width: 128, opacity: 1 } : {}}
          transition={{ delay: 1.2, duration: 0.8 }}
        />
      </motion.div>
    </section>
  );
}

export default SolutionSection;
